import type { CDMOApplication } from './supabase';
import type { MatchingWeights } from './matching';
import {
  BASE_SCORE_WEIGHTS,
  BATCH_SIZE_TIER_ORDER,
  BUDGET_BAND_ORDER,
  DEFAULT_TIER2_SLIDER_MULTIPLIER,
  DEVELOPMENT_STAGE_ORDER,
  SCORING_CROSS_REGION_PAIRINGS,
  SPEED_TO_START_ORDER,
} from '../constants/scoring';
import { LEGACY_SUPPORT_SERVICE_ALIASES } from '../constants';
import { formatMatchPercentageForDisplay } from '../utils/formatMatchScoreDisplay';
import { withAsciiDashes } from '../utils/labelMatching';

export {
  BASE_SCORE_WEIGHTS,
  BATCH_SIZE_TIER_ORDER,
  DEFAULT_TIER2_SLIDER_MULTIPLIER,
  DEVELOPMENT_STAGE_ORDER,
  SPEED_TO_START_ORDER,
} from '../constants/scoring';

export type ScoringDimension = keyof typeof BASE_SCORE_WEIGHTS;
export type BaseScoreWeights = Record<ScoringDimension, number>;

/**
 * Returns true when both regions are on the same continent cluster.
 * Used to award partial geography points.
 */
function areSameRegionCluster(a: string, b: string): boolean {
  return SCORING_CROSS_REGION_PAIRINGS[a]?.includes(b) ?? false;
}

/** Index of a label in an ordered option list, ignoring dash-character differences. */
function orderIndex(order: readonly string[], label: string): number {
  const target = withAsciiDashes(label);
  return order.findIndex(entry => withAsciiDashes(entry) === target);
}

/**
 * Finds the index of a label within a tier order list, allowing for robust
 * matching even when labels have extra qualifiers (like units or "Clinical").
 */
function findTierIndex(order: readonly string[], label: string | undefined): number {
  if (!label) return -1;

  // Try exact match first
  const exact = orderIndex(order, label);
  if (exact !== -1) return exact;

  const normalized = label.split(' (')[0].trim().toLowerCase();

  // Try matching by base name (e.g. "Clinical Scale" or "Phase I")
  return order.findIndex(tier => {
    const tierBase = tier.split(' (')[0].trim().toLowerCase();
    return tierBase === normalized || tierBase.startsWith(normalized) || normalized.startsWith(tierBase);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Input / Output interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The fields from the biotech project request that participate in scoring.
 * These come from the matchmaking sidebar / intake form.
 */
export interface ScoringRequest {
  /** Biotech's required batch size (one of {@link BATCH_SIZE_TIER_ORDER}). */
  batchSize?: string;

  /**
   * Biotech's development stage (one of {@link DEVELOPMENT_STAGE_ORDER}).
   * Maps from biotech signup `development_stage` or sidebar `engagement_scales`.
   */
  developmentStage?: string;

  /**
   * Maximum acceptable onboarding timeline (one of {@link SPEED_TO_START_ORDER}).
   * Lower index = faster = stricter requirement.
   */
  speedToStart?: string;

  /**
   * Biotech's project budget — one of the BUDGET_RANGES_OPTIONS values.
   * e.g. '< $50k', '$50k - $150k', …, '$1.5M+'
   */
  budget?: string;

  /**
   * Biotech's preferred operating region(s).
   * e.g. ['North America', 'Europe']
   */
  operatingRegions?: string[];

  /**
   * Additional support services requested.
   * e.g. ['Regulatory Support', 'Tech Transfer']
   */
  supportServices?: string[];
}

/** Per-dimension score breakdown returned alongside the total. */
export interface DimensionScore {
  /**
   * Points earned: full floating precision (`ratio * maxWeight`). Format for UI with
   * `formatMatchBreakdownPointsForDisplay` (one decimal).
   */
  earned: number;
  /** Maximum available points for this dimension. */
  max: number;
  /** Score expressed as a ratio (0.0 to 1.0). */
  ratio: number;
  /** Human-readable explanation of why this score was awarded. */
  rationale: string;
}

/** Complete match score result for one CDMO. */
export interface CDMOMatchScore {
  /**
   * Raw earned points summed across ALL six dimensions (0–100).
   * Useful for debugging; do NOT use this for ranking or display.
   * Use {@link percentageScore} instead.
   */
  totalScore: number;

  /**
   * Weighted denominator for the match %: Σ (base max marks × slider multiplier)
   * for each dimension the user activated. Aligns with spreadsheet “Max Weighted Score”.
   * Zero when the request had no Tier-2 inputs.
   */
  activeWeightTotal: number;

  /**
   * Normalised score: Σ(earned×w) / Σ(max×w) × 100 over active dimensions.
   *
   * Only dimensions the user filled count toward numerator and denominator.
   *
   * Special rule: 100% is awarded **only** when the CDMO earns the maximum
   * possible points on every active dimension. Otherwise the ceiling is 99%.
   */
  percentageScore: number;

  /**
   * Same ratio as {@link percentageScore} but unrounded and uncapped.
   *
   * Use this for ranking: rounding to whole percent makes CDMOs that differ by a fraction
   * of a point compare equal, which freezes their relative order. Display
   * {@link percentageScore}.
   */
  rawPercentageScore: number;

  /** Per-dimension breakdown for UI tooltips. */
  breakdown: Record<ScoringDimension, DimensionScore>;
}

/**
 * Merge partial / dynamic base weights from DB with safe defaults.
 * Invalid or missing values are ignored.
 */
export function resolveBaseScoreWeights(
  weights?: Partial<BaseScoreWeights> | null
): BaseScoreWeights {
  const resolved: BaseScoreWeights = { ...BASE_SCORE_WEIGHTS };
  if (!weights) return resolved;

  for (const [key, value] of Object.entries(weights)) {
    if (
      key in resolved &&
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= 0
    ) {
      resolved[key as ScoringDimension] = value;
    }
  }

  return resolved;
}

// ─────────────────────────────────────────────────────────────────────────────
// Budget compatibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a 0–1 budget compatibility ratio.
 *
 * Scoring logic:
 *   - Project budget >= CDMO's lowest budget band → 1.0 (full)
 *   - Project budget is one band below → 0.5 (partial)
 *   - Project budget is two+ bands below → 0.0 (zero)
 *
 * "Below" means the project has less money than the CDMO's minimum.
 */
export function scoreBudgetCompatibility(
  projectBudget: string,
  cdmoBudgetRanges: string[]
): number {
  if (!projectBudget || cdmoBudgetRanges.length === 0) return 0;

  const projectIndex = findTierIndex(BUDGET_BAND_ORDER, projectBudget);
  if (projectIndex === -1) return 0;

  // Find the lowest (cheapest) budget band the CDMO accepts
  const cdmoIndexes = cdmoBudgetRanges
    .map((b) => findTierIndex(BUDGET_BAND_ORDER, b))
    .filter((i) => i !== -1);

  if (cdmoIndexes.length === 0) return 0;

  const cdmoMinIndex = Math.min(...cdmoIndexes);

  const gap = cdmoMinIndex - projectIndex; // positive = CDMO is more expensive than the project budget

  if (gap <= 0)  return 1.0; // project budget covers or exceeds CDMO minimum
  if (gap === 1) return 0.5; // one band off
  return 0;                  // two or more bands off
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoring functions (one per dimension)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Batch Size scoring
 *
 * - Exact tier match → ratio 1.0
 * - One tier off     → ratio 0.5
 * - Two+ tiers off   → ratio 0
 */
export function scoreBatchSize(
  requestedSize: string | undefined,
  cdmoSizes: string[],
  maxWeight: number = BASE_SCORE_WEIGHTS.batch_size,
): DimensionScore {
  if (!requestedSize) {
    return {
      earned: 0,
      max: maxWeight,
      ratio: 0,
      rationale: 'No batch size was selected, so this category was not scored.',
    };
  }

  if (cdmoSizes.length === 0) {
    return {
      earned: 0,
      max: maxWeight,
      ratio: 0,
      rationale: 'No compatible batch size tier was found for your requirement.',
    };
  }

  const requestedIndex = findTierIndex(BATCH_SIZE_TIER_ORDER, requestedSize);
  if (requestedIndex === -1) {
    return {
      earned: 0,
      max: maxWeight,
      ratio: 0,
      rationale: 'No compatible batch size tier was found for your requirement.',
    };
  }

  // Find the closest CDMO tier
  let minDistance = Infinity;
  for (const size of cdmoSizes) {
    const idx = findTierIndex(BATCH_SIZE_TIER_ORDER, size);
    
    if (idx !== -1) {
      minDistance = Math.min(minDistance, Math.abs(requestedIndex - idx));
    }
  }

  if (!Number.isFinite(minDistance)) {
    return {
      earned: 0,
      max: maxWeight,
      ratio: 0,
      rationale: 'No compatible batch size tier was found for your requirement.',
    };
  }

  let ratio: number;
  let rationale: string;

  if (minDistance === 0) {
    ratio = 1.0;
    rationale = "Your batch size requirement matches this partner's listed capabilities.";
  } else if (minDistance === 1) {
    ratio = 0.5;
    rationale =
      'This partner is one batch-size tier away from your selection—a close fit on our scale, but not an exact match.';
  } else {
    ratio = 0;
    rationale = 'This CDMO does not meet your requested batch size range.';
  }

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

/**
 * Development Stage scoring
 *
 * - Exact stage    → ratio 1.0
 * - Adjacent stage → ratio 0.5
 * - Distant stage  → ratio 0
 */
export function scoreDevelopmentStage(
  requestedStage: string | undefined,
  cdmoEngagementScales: string[],
  maxWeight: number = BASE_SCORE_WEIGHTS.development_stage,
): DimensionScore {
  if (!requestedStage || cdmoEngagementScales.length === 0) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'No development stage data available for comparison.' };
  }

  const requestedIndex = findTierIndex(DEVELOPMENT_STAGE_ORDER, requestedStage);
  if (requestedIndex === -1) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: `Unknown development stage: "${requestedStage}".` };
  }

  let minDistance = Infinity;
  for (const scale of cdmoEngagementScales) {
    const idx = findTierIndex(DEVELOPMENT_STAGE_ORDER, scale);
    if (idx !== -1) {
      minDistance = Math.min(minDistance, Math.abs(requestedIndex - idx));
    }
  }

  if (!Number.isFinite(minDistance)) {
    return {
      earned: 0,
      max: maxWeight,
      ratio: 0,
      rationale:
        'No comparable development stage on this profile (labels may differ from the matching scale).',
    };
  }

  let ratio: number;
  let rationale: string;

  if (minDistance === 0) {
    ratio = 1.0;
    rationale = 'Exact development stage match.';
  } else if (minDistance === 1) {
    ratio = 0.5;
    rationale = 'Adjacent development stage to your requirement.';
  } else {
    ratio = 0;
    rationale = `Development stage is ${minDistance} steps from your requirement.`;
  }

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

/**
 * Speed to Start scoring (progressive penalty)
 *
 * If the biotech selects **Project Dependent** (UI: “Flexible / Planning stage”), they impose no
 * fixed timeline → **ratio 1.0** for any CDMO speed.
 *
 * If the CDMO lists Project Dependent but the biotech chose a concrete timeline → ratio 0.5 (neutral).
 *
 * Otherwise: CDMO at or faster than the requirement → ratio 1.0; each step slower reduces the
 * ratio (progressive schedule below).
 *
 * Penalty schedule (steps slower, strict user ask):
 *   0 steps  → 20 pts (100%)
 *   1 step   → 15 pts (75%)
 *   2 steps  → 10 pts (50%)
 *   3 steps  →  5 pts (25%)
 *   4+ steps →  0 pts (0%)
 */
export function scoreSpeedToStart(
  requiredSpeed: string | undefined,
  cdmoSpeed: string | undefined,
  maxWeight: number = BASE_SCORE_WEIGHTS.speed_to_start,
): DimensionScore {
  // An unstated timeline earns nothing: the requested criterion is unmet because the partner never
  // committed to it. The dimension keeps its max so it still counts against the request, which is
  // what stops a silent profile outscoring one that answered.
  if (!requiredSpeed || !cdmoSpeed) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'No speed-to-start data available for comparison.' };
  }

  const requiredIndex = orderIndex(SPEED_TO_START_ORDER, requiredSpeed);
  const cdmoIndex     = orderIndex(SPEED_TO_START_ORDER, cdmoSpeed);

  if (requiredIndex === -1) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: `Unknown required speed: "${requiredSpeed}".` };
  }
  if (cdmoIndex === -1) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: `Unknown CDMO speed: "${cdmoSpeed}".` };
  }

  let ratio: number;
  let rationale: string;

  const pdLabel = SPEED_TO_START_ORDER[SPEED_TO_START_ORDER.length - 1];

  // User chose flexible / planning (Project Dependent) → no timeline constraint; full credit.
  if (requiredSpeed === pdLabel) {
    ratio = 1.0;
    rationale =
      'You selected a flexible / planning-stage start — partner timelines satisfy this criterion.';
  } else if (cdmoSpeed === pdLabel) {
    // Strict user ask, CDMO lists project-dependent speed → neutral partial credit
    ratio = 0.5;
    rationale = 'Partner lists speed as project dependent relative to your timeline.';
  } else {
    const stepsSlower = cdmoIndex - requiredIndex; // positive = CDMO is slower than requirement

    if (stepsSlower <= 0) {
      ratio = 1.0;
      rationale = 'CDMO can start at or faster than your timeline.';
    } else {
      // Progressive penalty: each step beyond the requirement reduces score by 25 % of max
      ratio = Math.max(0, 1 - stepsSlower * 0.25);
      rationale = `CDMO is ${stepsSlower} step(s) slower than your timeline.`;
    }
  }

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

/**
 * Budget scoring
 *
 * Score based on compatibility between project budget and CDMO minimum engagement cost.
 *   - Compatible (project >= CDMO minimum) → ratio 1.0
 *   - One band below                       → ratio 0.5
 *   - Two+ bands below                     → ratio 0
 */
export function scoreBudget(
  projectBudget: string | undefined,
  cdmoBudgetRanges: string[],
  maxWeight: number = BASE_SCORE_WEIGHTS.budget,
): DimensionScore {
  if (!projectBudget || cdmoBudgetRanges.length === 0) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'No budget data available for comparison.' };
  }

  const ratio = scoreBudgetCompatibility(projectBudget, cdmoBudgetRanges);

  const rationale =
    ratio === 1.0 ? 'Project budget aligns with the CDMO’s typical minimum engagement cost.' :
    ratio === 0.5 ? 'Project budget is one band below the CDMO’s typical minimum.' :
                    'Project budget is well below the CDMO’s typical minimum.';

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

/**
 * Geography scoring
 *
 * - Same-region overlap           → ratio 1.0
 * - Cross-region cluster pairing → ratio 0.5
 * - No overlap                   → ratio 0
 */
export function scoreGeography(
  requestedRegions: string[] | undefined,
  cdmoRegions: string[],
  maxWeight: number = BASE_SCORE_WEIGHTS.geography,
): DimensionScore {
  if (!requestedRegions || requestedRegions.length === 0 || cdmoRegions.length === 0) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'No geography data available for comparison.' };
  }

  let ratio: number;
  let rationale: string;

  // Check for direct same-region overlap
  const hasExactOverlap = requestedRegions.some((r) => cdmoRegions.includes(r));
  if (hasExactOverlap) {
    ratio = 1.0;
    rationale = 'CDMO operates in the same region as your preference.';
  } else {
    // Check for adjacent cross-region cluster
    const hasPartialOverlap = requestedRegions.some((r) =>
      cdmoRegions.some((c) => areSameRegionCluster(r, c))
    );
    if (hasPartialOverlap) {
      ratio = 0.5;
      rationale = 'CDMO is in an adjacent / paired region relative to your preference.';
    } else {
      ratio = 0;
      rationale = 'No geographic overlap with your preferred regions.';
    }
  }

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

/** Alias table in lookup form, built once at module load. See {@link normalizeSupportServiceLabel}. */
const LEGACY_SUPPORT_SERVICE_ALIAS_MAP: ReadonlyMap<string, string> = new Map(
  Object.entries(LEGACY_SUPPORT_SERVICE_ALIASES)
);

/**
 * Canonical comparison key for a support-service label.
 *
 * Resolves pre-rename labels to their current name so a CDMO whose profile still says
 * "Full Manufacturing" keeps matching a request for "Commercial Manufacturing". Mirrors
 * `normalizeRegulatoryCertificationLabel` and `normalizeDetailedCapabilityLabel` in
 * {@link ../lib/gatekeeperFilters}.
 *
 * Idempotent: no alias target is itself an alias key, so applying it twice is a no-op.
 *
 * @param value - Raw stored or requested label
 * @returns The current canonical label
 */
export function normalizeSupportServiceLabel(value: string): string {
  // `preferred_project_types` is a Postgres text[], which permits NULL elements, and this runs over
  // every CDMO in the ranking loop — a bare `value.trim()` would take the whole results page down
  // on one malformed row. Mirrors the `String(s)` coercion in `gatekeeperFilters`.
  const trimmed = String(value ?? '').trim();
  // Map, not a bare object index: a stored label of 'toString' would otherwise resolve up the
  // prototype chain and return a function, which is not nullish, so `??` would not fall through.
  return LEGACY_SUPPORT_SERVICE_ALIAS_MAP.get(trimmed) ?? trimmed;
}

/**
 * Support Services scoring
 *
 * Evaluates overlap between the user's requested support services and the
 * CDMO's offered project types.
 *
 * Scoring:
 *   - All requested services matched → ratio 1.0
 *   - Some matched                   → proportional ratio
 *   - None matched                   → ratio 0
 */
export function scoreSupportServices(
  requestedServices: string[] | undefined,
  cdmoProjectTypes: string[],
  maxWeight: number = BASE_SCORE_WEIGHTS.support_services,
): DimensionScore {
  if (!requestedServices || requestedServices.length === 0) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'No support services requested.' };
  }
  if (cdmoProjectTypes.length === 0) {
    return { earned: 0, max: maxWeight, ratio: 0, rationale: 'CDMO lists no project types / support services.' };
  }

  // Normalized on both sides so a pre-rename value keeps matching, whether it is the CDMO's stored
  // project type or the sponsor's saved request.
  const offered = new Set(cdmoProjectTypes.map(normalizeSupportServiceLabel));
  const matched = requestedServices.filter((s) => offered.has(normalizeSupportServiceLabel(s)));
  const ratio = matched.length / requestedServices.length;

  const rationale =
    ratio === 1.0
      ? `Covers all requested support areas: ${matched.join(', ')}.`
      : ratio > 0
        ? `Covers ${matched.length} of ${requestedServices.length} requested support areas (${matched.join(', ')}).`
        : 'Does not list the requested support areas.';

  return { earned: ratio * maxWeight, max: maxWeight, ratio, rationale };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main scoring function
// ─────────────────────────────────────────────────────────────────────────────

const DIMENSION_SLIDER_KEYS: Record<ScoringDimension, keyof MatchingWeights> = {
  batch_size: 'typical_batch_sizes',
  development_stage: 'engagement_scales',
  speed_to_start: 'speed_to_start',
  budget: 'budget',
  geography: 'operating_regions',
  support_services: 'preferred_project_types',
};

/**
 * “User applied weight” from the spreadsheet: multiplier on that row’s max marks and obtained points.
 */
function tier2UserAppliedWeight(
  customWeights: Partial<MatchingWeights> | undefined,
  dim: ScoringDimension
): number {
  const key = DIMENSION_SLIDER_KEYS[dim];
  const raw = customWeights?.[key];
  if (raw === undefined || raw === null || !Number.isFinite(Number(raw))) {
    return DEFAULT_TIER2_SLIDER_MULTIPLIER;
  }
  return Math.max(0, Number(raw));
}

/**
 * Match % = (Σ obtained×w) / (Σ max marks×w) × 100 over active Tier-2 dimensions (Excel model).
 * Example: max marks 20, slider w = 2 → max weighted marks 40; obtained 16 → weighted score 32.
 *
 * @param cdmo - CDMO profile from Supabase
 * @param request - Tier-2 filter inputs from the sidebar
 * @param customWeights - Per-dimension multipliers (sidebar sliders); missing keys use {@link DEFAULT_TIER2_SLIDER_MULTIPLIER}
 */
export function computeCDMOMatchScore(
  cdmo: CDMOApplication,
  request: ScoringRequest,
  customWeights?: Partial<MatchingWeights>,
  baseScoreWeights?: Partial<BaseScoreWeights> | null
): CDMOMatchScore {
  const defaultBaseWeights = resolveBaseScoreWeights(baseScoreWeights);

  // ── 1. Score each dimension on canonical base max marks (max marks column — not scaled by slider).
  const breakdown: CDMOMatchScore['breakdown'] = {
    batch_size: scoreBatchSize(request.batchSize, cdmo.typical_batch_sizes || [], defaultBaseWeights.batch_size),
    development_stage: scoreDevelopmentStage(
      request.developmentStage,
      cdmo.engagement_scales || [],
      defaultBaseWeights.development_stage
    ),
    speed_to_start: scoreSpeedToStart(request.speedToStart, cdmo.speed_to_start, defaultBaseWeights.speed_to_start),
    budget: scoreBudget(request.budget, cdmo.budget_ranges || [], defaultBaseWeights.budget),
    geography: scoreGeography(request.operatingRegions, cdmo.operating_regions || [], defaultBaseWeights.geography),
    support_services: scoreSupportServices(
      request.supportServices,
      cdmo.preferred_project_types || [],
      defaultBaseWeights.support_services
    ),
  };

  // ── 2. Active dimensions (user provided Tier-2 input)
  const activeDimensions: ScoringDimension[] = [];
  if (request.batchSize) activeDimensions.push('batch_size');
  if (request.developmentStage) activeDimensions.push('development_stage');
  if (request.speedToStart) activeDimensions.push('speed_to_start');
  if (request.budget) activeDimensions.push('budget');
  if (request.operatingRegions?.length) activeDimensions.push('geography');
  if (request.supportServices?.length) activeDimensions.push('support_services');

  // ── 3. Excel-style weighted total: Σ(weighted score) / Σ(max weighted marks), same w per row
  let weightedEarnedSum = 0;
  let weightedMaxSum = 0;
  for (const dim of activeDimensions) {
    const w = tier2UserAppliedWeight(customWeights, dim);
    const rowMax = breakdown[dim].max;
    weightedEarnedSum += breakdown[dim].earned * w;
    weightedMaxSum += rowMax * w;
  }

  const totalScore = (Object.values(breakdown) as DimensionScore[]).reduce((sum, d) => sum + d.earned, 0);

  let percentageScore = 0;
  let rawPercentageScore = 0;
  if (weightedMaxSum > 0) {
    const isPerfectMatch = Math.abs(weightedEarnedSum - weightedMaxSum) < 1e-5;
    const raw = (weightedEarnedSum / weightedMaxSum) * 100;
    rawPercentageScore = isPerfectMatch ? 100 : raw;
    percentageScore = isPerfectMatch
      ? 100
      : Math.min(formatMatchPercentageForDisplay(raw), 99);
  }

  return {
    totalScore,
    activeWeightTotal: weightedMaxSum,
    percentageScore,
    rawPercentageScore,
    breakdown,
  };
}

/**
 * Score and rank a list of eligible CDMOs (post-Tier-1 gatekeeper) against
 * a project request.
 *
 * CDMOs are sorted by {@link CDMOMatchScore.percentageScore} descending
 * (the normalised score, not the raw total) so that partial-input requests
 * produce fair comparisons.
 *
 * @param cdmos   - Eligible CDMOs (already filtered by Tier 1 gatekeeper)
 * @param request - Biotech project requirements
 * @returns CDMOs sorted by percentageScore descending, each annotated with its score
 */
export function rankCDMOs(
  cdmos: CDMOApplication[],
  request: ScoringRequest,
  customWeights?: Partial<MatchingWeights>,
  baseScoreWeights?: Partial<BaseScoreWeights> | null
): Array<{ cdmo: CDMOApplication; score: CDMOMatchScore }> {
  return cdmos
    .map((cdmo) => ({
      cdmo,
      score: computeCDMOMatchScore(cdmo, request, customWeights, baseScoreWeights),
    }))
    .sort((a, b) => b.score.percentageScore - a.score.percentageScore);
}
